/* eslint-disable no-unused-vars */
import { Component } from "react";
import FrcContext from "./frc";

function hashString(string: any) {
  let hash = 0;
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < string.length; i++) {
    // eslint-disable-next-line no-bitwise
    hash = (((hash << 5) - hash) + string.charCodeAt(i)) & 0xFFFFFFFF;
  }
  return hash;
}

const ComponentDecorator = (WrappedComponent: any) => {
  interface defaultPropsTypes {
    disabled?: boolean,
    validatePristine?: boolean,
    name: string,
    onChange: (inputIdentifier: string, value:string) => void,
    onFocus?: () => void,
    onBlur?: () => void,
    type?: string,
    label?: string | JSX.Element,
    placeholder?: string,
    addonBefore?: JSX.Element,
    addonBeforeE?: JSX.Element,
    id?: string,
    options?: { value: string; label: string; }[],
    rows?: number,
    cols?: number,
    style?: object,
    classes?: string,
    help?: JSX.Element,
    floatingLabel?: boolean,
  }
  class Wrapper extends Component<defaultPropsTypes> {
    static defaultProps = {
      disabled: false,
      validatePristine: false,
      onChange: (inputIdentifier, value) => {},
      onFocus: () => {},
      onBlur: () => {},
    };

  static displayName = `Wrapper(${WrappedComponent.displayName})`;

  static contextType = FrcContext;

  constructor(props: any) {
    super(props);
    this.state = { isTouched: false };
  }

  /**
     * Accessors for "special" properties.
     *
     * The following methods are used to merge master default properties that
     * are optionally set on the parent form. This to to allow customising these
     * properties 'as a whole' for the form, while retaining the ability to
     * override the properties on a component basis.
     *
     * Also see the parent-context mixin.
     */
  getLayout = () => {
    const defaultProperty = this.context.layout || "horizontal";

    return this.props.layout ? this.props.layout : defaultProperty;
  };

  getValidatePristine = () => {
    const defaultProperty = this.context.validatePristine || false;

    return this.props.validatePristine ? this.props.validatePristine : defaultProperty;
  };

  getRowClassName = () => [this.context.rowClassName, this.props.rowClassName];

  getLabelClassName = () => [this.context.labelClassName, this.props.labelClassName];

  getElementWrapperClassName = () => [this.context.elementWrapperClassName, this.props.elementWrapperClassName];

  getRowProperties = () => ({

    label: this.props.label,
    rowClassName: this.getRowClassName(),
    labelClassName: this.getLabelClassName(),
    elementWrapperClassName: this.getElementWrapperClassName(),
    layout: this.getLayout(),

    required: this.props.isRequired,
    hasErrors: this.showErrors(),

    isValid: this.props.isValid,

    isTouched: this.state.isTouched,
  });

  /**
 * getId
 *
 * The ID is used as an attribute on the form control, and is used to allow
 * associating the label element with the form control.
 *
 * If we don't explicitly pass an `id` prop, we generate one based on the
 * `name` and `label` properties.
 */
  getId = () => {
    if (this.props.id) {
      return this.props.id;
    }

    const label = (typeof this.props.label === "undefined" ? "" : this.props.label);
    return [
      "frc",

      this.props.name.split("[").join("_").replace("]", ""),
      hashString(JSON.stringify(label)),
    ].join("-");
  };

  renderHelp = () => {
    if (!this.props.help) {
      return null;
    }
    return (

      <span className="help-block">{this.props.help}</span>
    );
  };

  renderErrorMessage = () => {
    if (!this.showErrors()) {
      return null;
    }

    const errorMessages = this.props.errorMessages || [];
    return errorMessages.map((message: any, key: any) => (
      <span key={key} className="help-block validation-message">{message}</span>
    ));
  };

  showErrors = () => {
    if (this.props.isPristine === true) {
      if (this.getValidatePristine() === false) {
        return false;
      }
    }

    return (this.props.isValid === false);
  };

  setTouched = (isTouched: any) => {
    this.setState({ isTouched });
  };

  render() {
    return (
      <WrappedComponent
        {...this.props}
        {...this.state}
        getId={this.getId}
        getLayout={this.getLayout}
        showErrors={this.showErrors}
        getRowProperties={this.getRowProperties}
        renderHelp={this.renderHelp}
        renderErrorMessage={this.renderErrorMessage}
        setTouched={this.setTouched}
      />
    );
  }
  }
  return Wrapper;
};

export default ComponentDecorator;
